// src/components/ResetPassword.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config';
import Footer from './Footer';
import './ResetPassword.css';

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tokenValid, setTokenValid] = useState(true);

  useEffect(() => {
    // Validate token on component mount
    if (!token) {
      setTokenValid(false);
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate passwords
    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match. Please try again.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.post(`${API_BASE_URL}/auth/reset-password/${token}`, {
        password
      });
      
      setSuccess('Password has been reset successfully! You can now login with your new password.');
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (err) {
      console.error('Reset password error:', err);
      if (err.response?.status === 400) {
        setError('Password reset link is invalid or has expired. Please request a new password reset.');
        setTokenValid(false);
      } else {
        setError(err.response?.data?.message || 'An error occurred while resetting your password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (error) setError('');
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    if (error) setError('');
  };

  if (!tokenValid) {
    return (
      <div className="reset-root">
        <div className="reset-content">
        <div className="reset-container">
          <div className="reset-error">
            <h1>Invalid Reset Link</h1>
            <p>{error}</p>
            <div className="reset-actions">
              <Link to="/forgot-password" className="reset-btn">
                Request New Reset
              </Link>
              <Link to="/login" className="reset-back-btn">
                Back to Login
              </Link>
            </div>
          </div>
        </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="reset-root">
      <div className="reset-content">
      <div className="reset-container">
        <div className="reset-left">
        <img 
                src="/Catalyst.png" 
                alt="Continuum Logo" 
                style={{ 
                  width: 'auto', 
                  height: 'auto',
                  maxWidth: '300px',
                  marginBottom: '50px'
                }} 
              />
          <h2 className="reset-title">Reset Your Password</h2>
          <p className="reset-desc">Enter your new password to complete the reset process.</p>
        </div>
        
        <div className="reset-right">
          <form className="reset-form" onSubmit={handleSubmit}>
            <h1 className="reset-form-title">New Password</h1>
            
            {error && (
              <div className="reset-error">
                <p>{error}</p>
              </div>
            )}
            
            {success && (
              <div className="reset-success">
                <p>{success}</p>
                <p>Redirecting to login page...</p>
              </div>
            )}
            
            {!success && (
              <>
                <div className="form-group">
                  <label htmlFor="password">New Password</label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={handlePasswordChange}
                    required
                    autoFocus
                    placeholder="Enter your new password"
                    disabled={loading}
                    minLength="6"
                  />
                  <small>Password must be at least 6 characters long</small>
                </div>
                
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm New Password</label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                    required
                    placeholder="Confirm your new password"
                    disabled={loading}
                  />
                </div>
                
                <button 
                  className="reset-btn" 
                  type="submit"
                  disabled={loading || !password || !confirmPassword}
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
                
                <div className="reset-login">
                  <Link to="/login">Back to Login</Link>
                </div>
              </>
            )}
          </form>
        </div>
      </div>
      </div>
      <Footer />
    </div>
  );
};

export default ResetPassword; 